import { useState, useCallback } from 'react';
import Lightbox from '@/components/base/Lightbox';

interface LightboxImage {
  src: string;
  alt: string;
  caption?: string;
}

const certifications = [
  {
    icon: 'ri-shield-check-line',
    title: 'ISO 9001',
    subtitle: 'Sistema de Gestión de Calidad',
    description: 'Certificación internacional que garantiza procesos de calidad en todas las etapas de producción.',
  },
  {
    icon: 'ri-leaf-line',
    title: 'ISO 14001',
    subtitle: 'Sistema de Gestión Ambiental',
    description: 'Compromiso con prácticas de producción sostenibles y respetuosas con el medio ambiente.',
  },
  {
    icon: 'ri-heart-pulse-line',
    title: 'OHSAS 18001',
    subtitle: 'Seguridad y Salud Ocupacional',
    description: 'Estándares internacionales de seguridad industrial y protección de la salud de los trabajadores.',
  },
  {
    icon: 'ri-fire-line',
    title: 'HSE',
    subtitle: 'Salud, Seguridad y Ambiente',
    description: 'Sistema integrado de gestión HSE para operaciones con productos químicos industriales.',
  },
  {
    icon: 'ri-award-line',
    title: 'Empresa High-Tech',
    subtitle: 'Identificación Nacional',
    description: 'Reconocimiento del gobierno chino por su capacidad de innovación tecnológica e I+D avanzado.',
  },
  {
    icon: 'ri-oil-line',
    title: 'Certificación PetroChina',
    subtitle: 'Calidad para la Industria Petrolera',
    description: 'Productos aprobados y certificados por PetroChina para uso en campos petroleros de China.',
  },
];

const certImages: LightboxImage[] = [
  {
    src: 'https://rixingroup.cn/wp-content/uploads/2025/07/1752217261240.png',
    alt: 'Certificaciones y licencias de Rixin Group',
    caption: 'Certificaciones Rixin Group',
  },
  {
    src: 'https://rixingroup.cn/wp-content/uploads/2025/07/daca55d74d4709134aae72083a4f4a51.jpg',
    alt: 'Honores y calificaciones de Rixin Group',
    caption: 'Honores y Calificaciones',
  },
];

export default function RixinCertifications() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const openLightbox = useCallback((idx: number) => setLightboxIndex(idx), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const prevImage = useCallback(() => {
    setLightboxIndex((prev) => (prev !== null ? (prev === 0 ? certImages.length - 1 : prev - 1) : null));
  }, []);
  const nextImage = useCallback(() => {
    setLightboxIndex((prev) => (prev !== null ? (prev === certImages.length - 1 ? 0 : prev + 1) : null));
  }, []);

  return (
    <section className="bg-background-100 py-20 md:py-28 border-t border-background-200/60">
      <div className="w-full px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14 md:mb-18">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-6">
              <i className="ri-medal-line text-sm w-4 h-4 flex items-center justify-center" />
              Certificaciones y Calidad
            </div>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
              Estándares Internacionales <span className="text-accent-500">de Calidad</span>
            </h2>
            <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto">
              Rixin Group mantiene las certificaciones más exigentes del sector petroquímico,
              garantizando que cada lote de demulsificante cumpla con estándares internacionales.
            </p>
          </div>

          {/* Cert Images */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-14 md:mb-18">
            {certImages.map((img, idx) => (
              <div
                key={img.src}
                onClick={() => openLightbox(idx)}
                className="group relative rounded-lg overflow-hidden border border-background-200/60 bg-background-50 cursor-pointer select-none"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openLightbox(idx); }}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-auto object-contain select-none"
                  draggable="false"
                />
                {/* Anti-download overlay */}
                <div
                  className="absolute inset-0 z-10"
                  onContextMenu={(e) => e.preventDefault()}
                  aria-hidden="true"
                />
                <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-background-50/90 text-foreground-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
                  <i className="ri-zoom-in-line text-sm" />
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {certifications.map((cert) => (
              <div
                key={cert.title}
                className="bg-background-50 rounded-lg p-6 border border-background-200/60 hover:border-accent-300/60 transition-all duration-300 group"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                  <i className={`${cert.icon} text-xl`} />
                </div>
                <h3 className="font-heading text-base font-bold text-foreground-950 mb-1">{cert.title}</h3>
                <p className="text-accent-600 text-xs font-medium mb-3">{cert.subtitle}</p>
                <p className="text-foreground-500 text-sm leading-relaxed">{cert.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      <Lightbox
        images={certImages}
        index={lightboxIndex}
        onClose={closeLightbox}
        onPrev={prevImage}
        onNext={nextImage}
      />
    </section>
  );
}